import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { setSeoPageCache } from "@/lib/page-cache";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { SeoFaq } from "@/components/seo/SeoFaq";
import { SeoLinkList } from "@/components/seo/SeoLinkList";
import {
  breadcrumbListSchema,
  collectionPageSchema,
  faqPageSchema,
  jsonLdScript,
} from "@/lib/seo-schema";
import { INSURANCES, SPECIALTIES } from "@/data/heallexa";
import { citiesInState } from "@/data/us-cities";
import {
  SEO_INSURANCE_IDS,
  specialtySlug,
  stateFromNameSlug,
  stateNameSlug,
  toSlug,
} from "@/lib/seo-slugs";

const SITE = "https://heallexa.com";

export const Route = createFileRoute("/locations/$state/")({
  loader: async ({ params }) => {
    await setSeoPageCache();
    const state = stateFromNameSlug(params.state);
    if (!state) throw notFound();
    const cities = citiesInState(state.code);
    return { state, cities };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) return {};
    const { state } = loaderData;
    const title = `Healthcare Providers in ${state.name} | Heallexa`;
    const description = `Find doctors, dentists, therapists, chiropractors, and specialists across ${state.name}. Browse Heallexa's ${state.name} provider directory by city and specialty.`;
    const slug = stateNameSlug(state.name);
    const url = `${SITE}/locations/${slug}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        jsonLdScript(collectionPageSchema({ name: title, description, url })),
        jsonLdScript(
          breadcrumbListSchema([
            { name: "Home", url: "/" },
            { name: "Locations", url: "/locations" },
            { name: state.name, url: `/locations/${slug}` },
          ]),
        ),
        jsonLdScript(
          faqPageSchema([
            {
              q: `How do I find a doctor in ${state.name}?`,
              a: `Pick your city on this page to see Heallexa's ${state.name} provider directory. Filter by specialty and insurance to narrow down.`,
            },
            {
              q: `Which insurance plans are accepted in ${state.name}?`,
              a: `Most ${state.name} providers accept major plans including BCBS, Aetna, UnitedHealthcare, Cigna, Humana, and Medicare. Confirm with each office.`,
            },
            {
              q: `Is Heallexa free for patients?`,
              a: `Yes. Search, browse, and contact providers in ${state.name} for free, no signup required.`,
            },
          ]),
        ),
      ],
    };
  },
  component: StatePage,
  notFoundComponent: () => (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-semibold mb-2">State not found</h1>
        <Link to="/locations" className="text-primary underline">
          Browse all locations
        </Link>
      </div>
    </div>
  ),
});

function StatePage() {
  const { state, cities } = Route.useLoaderData();
  const stateSlug = stateNameSlug(state.name);
  const topSpecialties = SPECIALTIES.slice(0, 18);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-5xl px-6 py-16">
        <Breadcrumbs
          className="mb-6"
          items={[
            { name: "Home", href: "/" },
            { name: "Locations", href: "/locations" },
            { name: state.name },
          ]}
        />
        <header className="mb-10">
          <p className="text-sm font-medium text-primary mb-2">{state.code}</p>
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight text-foreground">
            Healthcare Providers in {state.name}
          </h1>
          <p className="mt-4 text-muted-foreground max-w-2xl">
            Browse doctors, dentists, therapists, chiropractors, and specialists across {state.name}.
            Pick a city below to see local providers, or search the full {state.name} directory.
          </p>
          <Link
            to="/search"
            search={{ location: state.name }}
            className="inline-block mt-6 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition"
          >
            Search all providers in {state.name}
          </Link>
        </header>

        {cities.length > 0 && (
          <SeoLinkList
            heading={`Cities in ${state.name}`}
            links={cities.map((c: { name: string }) => ({
              label: c.name,
              href: `/locations/${stateSlug}/${toSlug(c.name)}`,
            }))}
            columns={3}
            className="mb-8"
          />
        )}

        <SeoLinkList
          heading={`Top specialties in ${state.name}`}
          links={topSpecialties.map((s) => ({
            label: `${s} in ${state.name}`,
            href: `/specialties/${specialtySlug(s)}`,
          }))}
          columns={3}
          className="mb-8"
        />

        <SeoLinkList
          heading={`Insurance carriers in ${state.name}`}
          links={SEO_INSURANCE_IDS.map((id) => INSURANCES.find((i) => i.id === id))
            .filter(Boolean)
            .map((ins) => ({
              label: `${ins!.short} providers in ${state.name}`,
              href: `/insurance/${ins!.id}`,
            }))}
          columns={3}
          className="mb-8"
        />

        <SeoFaq
          className="mb-8"
          items={[
            {
              q: `How do I find a doctor in ${state.name}?`,
              a: `Pick your city on this page to browse ${state.name} providers. You can filter by specialty and insurance once you're on the city page.`,
            },
            {
              q: `Which insurance plans are accepted in ${state.name}?`,
              a: `Most ${state.name} providers accept major plans including BCBS, Aetna, UnitedHealthcare, Cigna, Humana, and Medicare.`,
            },
            {
              q: `Is Heallexa free?`,
              a: `Yes — search, browse, and contact providers in ${state.name} for free with no signup required.`,
            },
          ]}
        />
      </main>
      <FooterSection />
    </div>
  );
}
